/**
 * A choice step as the chat UI renders it (see backend/routes/antonio_steps.go):
 * only what's needed to render the question and its option cards. The
 * LLM-only fields never reach the browser.
 */
export interface AntonioStep {
	key: string;
	/** Unset for the reference image choice, which the assistant asks itself. */
	question?: string;
	/** 1 means single choice. */
	maxSelect: number;
	options: AntonioStepOption[];
}

export interface AntonioStepOption {
	id: string;
	label: string;
	/**
	 * Absolute, already thumbed: the widget renders on third-party origins.
	 * Only on a reference image with a single photo.
	 */
	imageUrl?: string;
}

/**
 * The funnel step the chat renders, as the server sends it after every turn
 * (the antonio.step event, see backend/routes/antonio_funnel.go). The client
 * knows nothing about the funnel beyond this: it starts in `start` and from
 * then on renders whichever step the server names.
 */
export type AntonioStepView =
	/** Client-side only: before the server has named any step. */
	| { key: 'start'; kind: 'start' }
	| { key: string; kind: 'text' }
	| ({ kind: 'choice' } & AntonioStep)
	/** allowChanges false: no "not yet" option, the visitor can only go on. */
	| { key: string; kind: 'confirm-generate'; allowChanges: boolean }
	| { key: string; kind: 'image' }
	| {
			key: string;
			kind: 'contact';
			showEmailInput: boolean;
			showNewsletterCheckbox: boolean;
			allowChanges: boolean;
	  }
	| { key: string; kind: 'confirm-publish'; allowChanges: boolean }
	| { key: string; kind: 'end'; galleryUrl?: string };

export const ANTONIO_START_STEP: AntonioStepView = { key: 'start', kind: 'start' };

/**
 * What the visitor does in a step, sent to /api/antonio-chat with the key of
 * the step it answers (see backend/routes/antonio_actions.go).
 */
export type AntonioInput =
	| { type: 'start' }
	| { type: 'text' }
	| { type: 'choice'; optionIds: string[] }
	| { type: 'confirm_generate'; confirmed: boolean }
	| { type: 'publish'; confirmed: boolean }
	| { type: 'contact'; name: string; email?: string; emailOptIn?: boolean };
